import React, {useState, useEffect, useCallback} from 'react'
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  IconButton,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {DatePicker, LocalizationProvider, TimePicker} from '@mui/x-date-pickers'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, {Dayjs} from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {RootState} from '../../../setup'
import {useSelector} from 'react-redux'
import {toast} from 'react-toastify'
const selectTimeZone = (state: RootState) => state.timeZone.timeZone
const selectAuth = (state: RootState) => state.auth
dayjs.extend(utc)
dayjs.extend(timezone)
const FrequencyDateRangePicker = ({
  isTemplate,
  isEntry,
  isEdit,
  isFullForm,
  formData,
  setFormData,
  isDeleted = false,
}) => {
  const maxDate = dayjs().endOf('day')
  const minDate = dayjs().subtract(5, 'year')
  const [noteType, setNoteType] = useState(formData.noteType || '')
  const [startDate, setStartDate] = useState(formData.startDate || null)
  const [endDate, setEndDate] = useState(formData.endDate || null)
  const [displayDateRange, setDisplayDateRange] = useState(formData.displayDateRange || '')
  const [timeIn, setTimeIn] = useState<dayjs.Dayjs | string | null>(
    formData.timeIn === '24 Hours'
      ? '24 Hours'
      : formData.timeIn
        ? dayjs(formData.timeIn, 'hh:mm A')
        : null
  )

  const [timeOut, setTimeOut] = useState<dayjs.Dayjs | string | null>(
    formData.timeOut === '24 Hours'
      ? '24 Hours'
      : formData.timeOut
        ? dayjs(formData.timeOut, 'hh:mm A')
        : null
  )
  const [timeError, setTimeError] = useState<string | null>(null)
  const [is24Hours, setIs24Hours] = useState(formData.timeIn === '24 Hours')
  const timeZone = useSelector(selectTimeZone)
  const auth = useSelector(selectAuth)

  const updateFormData = (newValues) => {
    setFormData((prev) => ({
      ...prev,
      ...newValues,
    }))
  }

  const updateFormDataFromLocalState = useCallback(() => {
    setFormData({
      ...formData,
      noteType: noteType,
      startDate: startDate,
      endDate: endDate,
      timeIn: timeIn,
      timeOut: timeOut,
    })
  }, [noteType, startDate, endDate, timeIn, timeOut])

  useEffect(() => {
    updateFormDataFromLocalState()
  }, [updateFormDataFromLocalState])

  const handleStartDateChange = async (newStartDate) => {
    if (!newStartDate) return

    if (noteType !== 'DAILY' && newStartDate.isAfter(maxDate)) {
      newStartDate = maxDate
    } else if (newStartDate.isBefore(minDate)) {
      newStartDate = minDate
    }

    let calculatedStartDate
    switch (noteType) {
      case 'DAILY':
        calculatedStartDate = newStartDate
        break
      case 'WEEKLY':
        calculatedStartDate = newStartDate.startOf('week')
        break
      case 'MONTHLY':
        calculatedStartDate = newStartDate.startOf('month')
        break
      case 'QUARTERLY':
        const quarterMonth = Math.floor(newStartDate.month() / 3) * 3
        calculatedStartDate = newStartDate.month(quarterMonth).startOf('month')
        break
      case 'YEARLY':
        calculatedStartDate = newStartDate.startOf('month')
        break
      default:
        calculatedStartDate = null
    }

    let calculatedEndDate
    switch (noteType) {
      case 'DAILY':
        calculatedEndDate = calculatedStartDate
        break
      case 'WEEKLY':
        calculatedEndDate = calculatedStartDate.add(6, 'days').endOf('day')
        break
      case 'MONTHLY':
        calculatedEndDate = calculatedStartDate.endOf('month')
        break
      case 'QUARTERLY':
        calculatedEndDate = calculatedStartDate.add(2, 'months').endOf('month')
        break
      case 'YEARLY':
        calculatedEndDate = calculatedStartDate.add(11, 'months').endOf('month')
        break
      default:
        calculatedEndDate = null
    }

    const now = dayjs()
    const applyCurrentTime = (inputDate, now) => {
      const base = dayjs(inputDate)
      return base.hour(now.hour()).minute(now.minute()).second(0).millisecond(0)
    }
    const formattedStartDate = applyCurrentTime(calculatedStartDate, now).utc().tz(timeZone)
    const formattedEndDate = applyCurrentTime(calculatedEndDate, now).utc().tz(timeZone)
    setStartDate(dayjs(formattedStartDate))
    setEndDate(dayjs(formattedEndDate))
    setDisplayDateRange(
      `${dayjs(calculatedStartDate).format('MM-DD-YYYY')} to ${dayjs(calculatedEndDate).format(
        'MM-DD-YYYY'
      )}`
    )
    updateFormData({
      startDate: formattedStartDate.toISOString(),
      endDate: formattedEndDate.toISOString(),
      displayDateRange: `${dayjs(calculatedStartDate).format('MM-DD-YYYY')} to ${dayjs(
        calculatedEndDate
      ).format('MM-DD-YYYY')}`,
    })
  }

  const getMaxDateBasedOnFrequency = (freq) => {
    const today = dayjs()
    switch (freq) {
      case 'MONTHLY':
        return today.endOf('month')
      case 'YEARLY':
        return today.subtract(1, 'year').endOf('year')
      case 'WEEKLY':
        return today.subtract(1, 'week').endOf('week')
      default:
        return today
    }
  }

  const getMaxDateForQuarterly = () => {
    const today = dayjs()
    const currentYear = today.year()
    const quarters = [
      {end: dayjs().year(currentYear).month(2).endOf('month')},
      {end: dayjs().year(currentYear).month(5).endOf('month')},
      {end: dayjs().year(currentYear).month(8).endOf('month')},
      {end: dayjs().year(currentYear).month(11).endOf('month')},
    ]
    for (let i = 3; i >= 0; i--) {
      if (quarters[i].end.isBefore(today) || quarters[i].end.isSame(today, 'day')) {
        return quarters[i].end
      }
    }
    return dayjs()
      .year(currentYear - 1)
      .month(11)
      .endOf('month')
  }
  return (
    <>
      {isEdit ? (
        <Box sx={{display: 'flex', gap: 2, ml: 1.5, mr: 1.5}}>
          <FormControl sx={{width: '100%'}} color='primary' variant='filled' required>
            <TextField
              value={noteType}
              sx={{background: 'white'}}
              label='Note Type'
              name='noteType'
              disabled
            />
          </FormControl>

          {(isFullForm || isEntry) && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {noteType === 'MONTHLY' ? (
                <TextField
                  sx={{background: 'white', width: '300px'}}
                  label='Selected Range'
                  value={`01-${startDate ? dayjs(startDate).format('MMMM') : ''} - ${
                    endDate ? dayjs(endDate).format('DD-MMMM-YYYY') : ''
                  }`}
                  disabled
                />
              ) : noteType === 'YEARLY' ? (
                <TextField
                  sx={{background: 'white', width: '300px'}}
                  label='Selected Range'
                  value={`${startDate ? dayjs(startDate).format('MM-DD-YYYY') : ''} to ${
                    endDate ? dayjs(endDate).format('MM-DD-YYYY') : ''
                  }`}
                  disabled
                />
              ) : noteType === 'QUARTERLY' ? (
                <TextField
                  sx={{background: 'white', width: '300px'}}
                  label='Selected Range'
                  value={`${startDate ? dayjs(startDate).format('MMMM-YYYY') : ''} to ${
                    endDate ? dayjs(endDate).format('MMMM-YYYY') : ''
                  }`}
                  disabled
                />
              ) : noteType === 'DAILY' ? (
                <>
                  <TextField
                    label='Selected Date'
                    sx={{background: 'white', width: '250px'}}
                    value={startDate ? dayjs(startDate).format('MM-DD-YYYY') : ''}
                    disabled
                  />
                  {auth?.user?.isCaregiver !== true && (
                    <>
                      <FormControl sx={{mt: 1}}>
                        <label style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                                     <input
                             type='checkbox'
                             checked={is24Hours}
                             onChange={(e) => {
                               const checked = e.target.checked
                               setIs24Hours(checked)

                               if (checked) {
                                 setTimeIn(null)
                                 setTimeOut(null)
                                 updateFormData({
                                   timeIn: '24 Hours',
                                   timeOut: '00 Hours',
                                 })
                               } else {
                                 // When unchecking, set timeOut to timeIn if timeIn exists
                                 setTimeOut(timeIn)
                                 updateFormData({
                                   timeIn: timeIn,
                                   timeOut: timeIn,
                                 })
                               }
                             }}
                             disabled={isDeleted}
                           />
                          Whole day
                        </label>
                      </FormControl>
                      <TimePicker
                        label='Time In'
                        value={!is24Hours && dayjs.isDayjs(timeIn) ? timeIn : null}
                        onChange={(newValue: Dayjs | null) => {
                          if (!newValue || is24Hours) return

                          setTimeIn(newValue)
                          updateFormData({timeIn: newValue.format('hh:mm A')})

                          if (!dayjs.isDayjs(timeOut) || newValue.isAfter(timeOut)) {
                            const autoTimeOut = newValue.add(1, 'hour')
                            setTimeOut(autoTimeOut)
                            updateFormData({timeOut: autoTimeOut.format('hh:mm A')})
                          }

                          setTimeError(null)
                        }}
                        ampm
                        minutesStep={15}
                        sx={{background: 'white', width: '15%', mt: 0, ml: 0}}
                        disabled={is24Hours || auth?.user?.isCaregiver === true || isDeleted}
                        slotProps={{
                          field:{
                            readOnly: true,
                          },
                          textField: {
                            margin: 'normal',
                            InputProps: {
                              readOnly: is24Hours || auth?.user?.isCaregiver === true,
                              style: { cursor: 'pointer' },
                            },
                            onClick: (e) => {
                              // Prevent the click from bubbling up and trigger the date picker
                              e.preventDefault();
                              e.stopPropagation();
                              // Find and click the calendar icon to open the picker
                              const iconButton = e.currentTarget.querySelector('[data-testid="OpenPickerIconButton"]') || 
                                               e.currentTarget.querySelector('.MuiIconButton-root');
                              if (iconButton) {
                                (iconButton as HTMLElement).click();
                              }
                            }
                          },
                        }}
                      />

                      <TimePicker
                        label='Time Out'
                        value={!is24Hours && dayjs.isDayjs(timeOut) ? timeOut : null}
                        onChange={(newValue: Dayjs | null) => {
                          if (!newValue || is24Hours) return

                          if (dayjs.isDayjs(timeIn) && newValue.isBefore(timeIn)) {
                            const autoTimeOut = timeIn.add(1, 'hour')
                            setTimeOut(autoTimeOut)
                            updateFormData({timeOut: autoTimeOut.format('hh:mm A')})

                            const warningMsg =
                              'Time Out cannot be before Time In — auto-adjusted to one hour after Time In.'
                            setTimeError(warningMsg)
                            toast.warn(warningMsg)
                            return
                          }

                          setTimeOut(newValue)
                          updateFormData({timeOut: newValue.format('hh:mm A')})
                          setTimeError(null)
                        }}
                        ampm
                        minutesStep={15}
                        minTime={dayjs.isDayjs(timeIn) ? timeIn : undefined}
                        sx={{background: 'white', width: '15%', mt: 0}}
                        disabled={is24Hours || auth?.user?.isCaregiver === true || isDeleted}
                        slotProps={{
                          field:{
                            readOnly: true,
                          },
                          textField: {
                            margin: 'normal',
                            InputProps: {
                              readOnly: is24Hours || auth?.user?.isCaregiver === true,
                              style: { cursor: 'pointer' },
                            },
                            onClick: (e) => {
                              // Prevent the click from bubbling up and trigger the date picker
                              e.preventDefault();
                              e.stopPropagation();
                              // Find and click the calendar icon to open the picker
                              const iconButton = e.currentTarget.querySelector('[data-testid="OpenPickerIconButton"]') || 
                                               e.currentTarget.querySelector('.MuiIconButton-root');
                              if (iconButton) {
                                (iconButton as HTMLElement).click();
                              }
                            }
                          },
                        }}
                      />
                    </>
                  )}
                  {auth?.user?.isCaregiver === true && (
                    <TextField
                      label='Session Duration'
                      sx={{width: '25%', mt: 0, ml: 0}}
                      value={
                        is24Hours
                          ? '24 Hours'
                          : timeIn && timeOut
                            ? (() => {
                                const diff = dayjs(timeOut).diff(dayjs(timeIn), 'minute')
                                const hrs = Math.floor(diff / 60)
                                const mins = diff % 60
                                return `${hrs} hrs ${mins} mins`
                              })()
                            : ''
                      }
                      fullWidth
                      margin='normal'
                      InputProps={{
                        readOnly: true,
                      }}
                      disabled
                    />
                  )}
                </>
              ) : noteType === 'WEEKLY' ? (
                <TextField
                  label='Selected Date'
                  sx={{background: 'white', width: '300px'}}
                  value={`${startDate ? dayjs(startDate).format('MM-DD-YY') : ''} to ${
                    endDate ? dayjs(endDate).format('MM-DD-YY') : ''
                  }`}
                  disabled
                />
              ) : null}
            </LocalizationProvider>
          )}
        </Box>
      ) : (
        (isFullForm || isTemplate || isEntry) && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{display: 'flex', gap: 2, alignItems: 'center', mb: 1, mr: 1.5}}>
              {(isTemplate || isFullForm) && (
                <FormControl variant='filled' sx={{background: 'white', width:'100%', mt: 0}}>
                  <InputLabel sx={{ml: 1.5}}>Select Note Type</InputLabel>
                  <Select
                    value={noteType}
                    onChange={(e) => {
                      const selected = e.target.value
                      setNoteType(selected)
                      setStartDate(null)
                      setEndDate(null)
                      setDisplayDateRange('')
                      setTimeIn(null)
                      setTimeOut(null)
                      updateFormData({
                        noteType: selected,
                        startDate: null,
                        endDate: null,
                        displayDateRange: '',
                        timeIn: null,
                        timeOut: null,
                      })
                    }}
                    required
                    disabled={isEdit || false}
                    sx={{mt: 0, ml: 2}}
                  >
                    {['DAILY', 'WEEKLY', 'MONTHLY', 'QUARTERLY', 'YEARLY'].map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {(isEntry || isFullForm) && noteType === 'DAILY' && (
                <>
                                     <TimePicker
                     label='Time In'
                     value={!is24Hours && dayjs.isDayjs(timeIn) ? timeIn : null}
                     onChange={(newValue: Dayjs | null) => {
                       if (!newValue || is24Hours) return

                       setTimeIn(newValue)
                       updateFormData({timeIn: newValue.format('hh:mm A')})

                       if (!dayjs.isDayjs(timeOut) || newValue.isAfter(timeOut)) {
                         const autoTimeOut = newValue.add(1, 'hour')
                         setTimeOut(autoTimeOut)
                         updateFormData({timeOut: autoTimeOut.format('hh:mm A')})
                       }

                       setTimeError(null)
                     }}
                     ampm
                     minutesStep={15}
                     sx={{background: 'white', width: '15%', mt: 1.5, ml: 1.5}}
                     disabled={is24Hours || isEdit || isDeleted}
                    slotProps={{
                      field:{
                        readOnly: true,
                      },
                      textField: {
                        margin: 'normal',
                        InputProps: {
                          readOnly: is24Hours || isEdit,
                          style: { cursor: 'pointer' },
                        },
                        onClick: (e) => {
                          // Prevent the click from bubbling up and trigger the date picker
                          e.preventDefault();
                          e.stopPropagation();
                          // Find and click the calendar icon to open the picker
                          const iconButton = e.currentTarget.querySelector('[data-testid="OpenPickerIconButton"]') || 
                                           e.currentTarget.querySelector('.MuiIconButton-root');
                          if (iconButton) {
                            (iconButton as HTMLElement).click();
                          }
                        }
                      },
                    }}
                  />
                  <TimePicker
                    label='Time Out'
                    value={!is24Hours && dayjs.isDayjs(timeOut) ? timeOut : null}
                    onChange={(newValue: Dayjs | null) => {
                    if (!newValue || is24Hours) return

                    if (dayjs.isDayjs(timeIn) && newValue.isBefore(timeIn)) {
                    const autoTimeOut = timeIn.add(1, 'hour')
                    setTimeOut(autoTimeOut)
                    updateFormData({timeOut: autoTimeOut.format('hh:mm A')})

                      const warningMsg =
                           'Time Out cannot be before Time In — auto-adjusted to one hour after Time In.'
                         setTimeError(warningMsg)
                         toast.warn(warningMsg)
                         return
                       }

                       setTimeOut(newValue)
                       updateFormData({timeOut: newValue.format('hh:mm A')})
                       setTimeError(null)
                     }}
                     ampm
                     minutesStep={15}
                     minTime={dayjs.isDayjs(timeIn) ? timeIn : undefined}
                     sx={{background: 'white', width: '15%', mt: 1.5}}
                     disabled={is24Hours || isEdit || isDeleted}
                    slotProps={{
                      field:{
                        readOnly: true,
                      },
                      textField: {
                        margin: 'normal',
                        InputProps: {
                          readOnly: is24Hours || isEdit,
                          style: { cursor: 'pointer' },
                        },
                        onClick: (e) => {
                          // Prevent the click from bubbling up and trigger the date picker
                          e.preventDefault();
                          e.stopPropagation();
                          // Find and click the calendar icon to open the picker
                          const iconButton = e.currentTarget.querySelector('[data-testid="OpenPickerIconButton"]') || 
                                           e.currentTarget.querySelector('.MuiIconButton-root');
                          if (iconButton) {
                            (iconButton as HTMLElement).click();
                          }
                        }
                      },
                    }}
                  />
                  <FormControl sx={{mt: 1.5}}>
                    <label style={{display: 'flex', alignItems: 'center', gap: 8}}>
                      <input
                        type='checkbox'
                        checked={is24Hours}
                        onChange={(e) => {
                          const checked = e.target.checked
                          setIs24Hours(checked)

                          if (checked) {
                            setTimeIn(null)
                            setTimeOut(null)
                            updateFormData({
                              timeIn: '24 Hours',
                              timeOut: '00 Hours',
                            })
                          } else {
                            // When unchecking, set timeOut to timeIn if timeIn exists
                            setTimeOut(timeIn)
                            updateFormData({
                              timeIn: timeIn,
                              timeOut: timeIn,
                            })
                          }
                        }}
                        disabled={isEdit}
                      />
                      Whole day
                    </label>
                  </FormControl>
                  <DatePicker
                    label='Select Date'
                    sx={{background: 'white', width: '25%', mt: 1}}
                    value={startDate}
                    minDate={minDate}
                    maxDate={maxDate}
                    onChange={handleStartDateChange}
                    slotProps={{
                      field:{
                        readOnly: true,
                      },
                      textField: {
                        margin: 'normal',
                        disabled: isEdit || false,
                        inputProps: { 
                          style: { cursor: 'pointer' },
                          readOnly: true,
                        },
                        onClick: (e) => {
                          // Prevent the click from bubbling up and trigger the date picker
                          e.preventDefault();
                          e.stopPropagation();
                          // Find and click the calendar icon to open the picker
                          const iconButton = e.currentTarget.querySelector('[data-testid="OpenPickerIconButton"]') || 
                                           e.currentTarget.querySelector('.MuiIconButton-root');
                          if (iconButton) {
                            (iconButton as HTMLElement).click();
                          }
                        }
                      },
                    }}
                  />
                </>
              )}

              {(isEntry || isFullForm) &&
                noteType !== 'DAILY' &&
                (noteType === 'WEEKLY' ||
                  noteType === 'MONTHLY' ||
                  noteType === 'QUARTERLY' ||
                  noteType === 'YEARLY') && (
                  <>
                    <DatePicker
                      views={
                        noteType === 'YEARLY'
                          ? ['year', 'month']
                          : noteType === 'MONTHLY' || noteType === 'QUARTERLY'
                            ? ['month', 'year']
                            : undefined
                      }
                      label={
                        noteType === 'YEARLY'
                          ? 'Select Year and Month'
                          : noteType === 'MONTHLY'
                            ? 'Select Month'
                            : noteType === 'QUARTERLY'
                              ? 'Select Quarter'
                              : 'Select Date'
                      }
                      sx={{background: 'white', width: '25%', mt: 1, mr: 0, ml: isEntry ? 1.5 : 0}}
                      maxDate={
                        noteType === 'QUARTERLY'
                          ? getMaxDateForQuarterly()
                          : getMaxDateBasedOnFrequency(noteType)
                      }
                      minDate={minDate}
                      value={startDate}
                      onChange={handleStartDateChange}
                      slotProps={{
                        field:{
                          readOnly: true,
                        },
                        textField: {
                          margin: 'normal',
                          disabled: isEdit || false,
                          inputProps: { 
                            style: { cursor: 'pointer' },
                            readOnly: true,
                          },
                          onClick: (e) => {
                            // Prevent the click from bubbling up and trigger the date picker
                            e.preventDefault();
                            e.stopPropagation();
                            // Find and click the calendar icon to open the picker
                            const iconButton = e.currentTarget.querySelector('[data-testid="OpenPickerIconButton"]') || 
                                             e.currentTarget.querySelector('.MuiIconButton-root');
                            if (iconButton) {
                              (iconButton as HTMLElement).click();
                            }
                          }
                        },
                      }}
                    />
                  </>
                )}
              {displayDateRange && noteType !== 'DAILY' && (
                <TextField
                  label='Selected Range'
                  sx={{width: '25%', mt: 1, ml: 0}}
                  value={displayDateRange}
                  fullWidth
                  margin='normal'
                  InputProps={{
                    readOnly: true,
                  }}
                />
              )}
              {['WEEKLY', 'QUARTERLY'].includes(noteType) && (
                <Tooltip
                  title={
                    noteType === 'QUARTERLY'
                      ? 'The start date will automatically adjust to the first month of the selected quarter.'
                      : 'The start date will automatically adjust to the first day of the selected week.'
                  }
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        fontSize: '0.95rem',
                        mt: 0,
                        ml: 0,
                      },
                    },
                  }}
                >
                  <IconButton size='small' sx={{mt: 1, color: '#ff5722'}}>
                    <InfoOutlinedIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </LocalizationProvider>
        )
      )}
    </>
  )
}

export default FrequencyDateRangePicker
